import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getAnonymousName, getAvatarForShadow } from '../utils/shadowIdentity';
import { ChevronLeft, Crown, Medal, Award, Heart, MessageCircle, Share2, Sparkles, User, BarChart2 } from 'lucide-react';

export const ShadowWeeklyLead = () => {
  const navigate = useNavigate();
  const [confessions, setConfessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCollege, setUserCollege] = useState('');
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        if (!currentUser) {
          navigate('/login');
          return;
        }

        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const college = userDoc.data().college;
          if (!college) {
             toast.error('Please update your profile with a college to see the leaderboard.');
             navigate('/');
             return;
          }
          setUserCollege(college);

          const q = query(
            collection(db, 'confessions'),
            where('college', '==', college),
            orderBy('likes', 'desc'),
            limit(10)
          );

          const snapshot = await getDocs(q);
          const list = snapshot.docs.map((docSnap, index) => ({
            id: docSnap.id,
            rank: index + 1,
            ...docSnap.data()
          }));
          setConfessions(list);
        }
      } catch (err) {
        console.error("Error fetching leaderboard", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [currentUser, navigate]);

  const getRankStyling = (rank) => {
    const baseStyle = {
      bgGradient: 'bg-[#161616]',
      borderColor: 'border-[#2a2a2a]',
      textColor: 'text-[#0BD9E5]',
      accentColor: '#0BD9E5',
      Icon: null,
      title: 'FEATURED'
    };

    switch (rank) {
      case 1:
        return {
          ...baseStyle,
          bgGradient: 'bg-gradient-to-b from-[#FFD700]/10 to-[#161616]',
          borderColor: 'border-[#FFD700]',
          textColor: 'text-[#FFD700]',
          accentColor: '#FFD700',
          Icon: Crown,
          title: 'TOP SHADOW'
        };
      case 2:
        return {
          ...baseStyle,
          bgGradient: 'bg-gradient-to-b from-[#C0C0C0]/10 to-[#161616]',
          borderColor: 'border-[#C0C0C0]',
          textColor: 'text-[#C0C0C0]',
          accentColor: '#C0C0C0',
          Icon: Medal,
          title: 'ELITE TITAN'
        };
      case 3:
        return {
          ...baseStyle,
          bgGradient: 'bg-gradient-to-b from-[#CD7F32]/10 to-[#161616]',
          borderColor: 'border-[#CD7F32]',
          textColor: 'text-[#CD7F32]',
          accentColor: '#CD7F32',
          Icon: Award,
          title: 'PRIME FLAME'
        };
      default:
        return baseStyle;
    }
  };

  if (loading) {
    return (
      <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
      </main>
    );
  }

  return (
    <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 pb-20 sm:pb-0">
      <div className="sticky top-0 bg-black/80 backdrop-blur-md z-40 border-b border-gray-800 flex items-center h-[60px] px-4">
        <button onClick={() => navigate('/')} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2">
          <ChevronLeft size={24} className="text-white" />
        </button>
        <h1 className="text-xl font-extrabold text-white">Weekly Flare</h1>
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex flex-col items-center mb-8 mt-4">
          <h2 className="text-[20px] font-bold text-white mb-3">{userCollege || 'Your College'}</h2>
          <div className="flex items-center w-full max-w-[80%] justify-center gap-3">
             <div className="h-px bg-[#333] flex-1"></div>
             <span className="text-[12px] font-bold text-[#0BD9E5] tracking-widest">HALL OF FAME</span>
             <div className="h-px bg-[#333] flex-1"></div>
          </div>
        </div>

        {confessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-[80px] h-[80px] bg-[#1a1a1a] rounded-full flex items-center justify-center mb-5 border border-[#333]">
              <Sparkles size={40} className="text-[#FFD700]" />
            </div>
            <h3 className="text-[20px] font-bold text-white mb-2">No Leaders Yet</h3>
            <p className="text-[#888] text-[14px] max-w-[250px]">
              Be the first to drop a shadow and claim the throne!
            </p>
          </div>
        ) : (
          <div className="flex flex-col">
             {/* Podium for Top 3 */}
             <div className="flex items-end justify-center gap-2 sm:gap-4 mb-10 mt-6 h-64">
                {[confessions[1], confessions[0], confessions[2]].map((item, idx) => {
                   if (!item) return <div key={idx} className="w-24 sm:w-32" />;
                   const styling = getRankStyling(item.rank);
                   const RankIcon = styling.Icon;
                   
                   let height = 'h-32';
                   let bg = 'bg-gradient-to-t from-[#C0C0C0]/20 to-transparent';
                   if (item.rank === 1) { height = 'h-40'; bg = 'bg-gradient-to-t from-[#FFD700]/30 to-transparent'; }
                   if (item.rank === 3) { height = 'h-28'; bg = 'bg-gradient-to-t from-[#CD7F32]/20 to-transparent'; }

                   return (
                      <div key={item.id} onClick={() => navigate(`/shadow/${item.id}`)} className="flex flex-col items-center cursor-pointer group w-28 sm:w-36 animate-fade-in-up">
                         <div className="relative mb-2 transition-transform group-hover:-translate-y-2 duration-300">
                            <div className={`absolute -top-4 -right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center bg-black border ${styling.borderColor}`}>
                               {RankIcon ? <RankIcon size={16} fill={styling.accentColor} className={styling.textColor} /> : <span className="text-white text-xs">#{item.rank}</span>}
                            </div>
                            <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 ${styling.borderColor} overflow-hidden bg-gray-900 mx-auto`}>
                               <img src={getAvatarForShadow(getAnonymousName(item.userId))} className="w-full h-full object-cover" />
                            </div>
                         </div>
                         <span className={`text-xs sm:text-sm font-bold mb-2 truncate w-full text-center ${styling.textColor}`}>
                            {getAnonymousName(item.userId)}
                         </span>
                         <div className={`w-full rounded-t-xl ${height} ${bg} border-t ${styling.borderColor} flex flex-col items-center justify-start pt-4 transition-all group-hover:brightness-125`}>
                            <div className="flex items-center gap-1 text-white/80">
                               <Heart size={14} className="fill-pink-500 text-pink-500" />
                               <span className="text-xs font-bold">{item.likes || 0}</span>
                            </div>
                            <p className="text-[11px] text-white/60 mt-2 px-2 line-clamp-2 text-center leading-tight font-medium">
                               {item.content}
                            </p>
                         </div>
                      </div>
                   )
                })}
             </div>

             {/* Ranks 4-10 */}
             {confessions.length > 3 && (
                 <div className="flex flex-col gap-3 px-2 pb-10">
                    <h3 className="text-gray-400 font-bold mb-2 text-sm tracking-widest uppercase">The Contenders</h3>
                    {confessions.slice(3).map(item => {
                        return (
                           <div key={item.id} onClick={() => navigate(`/shadow/${item.id}`)} className="flex items-center gap-4 bg-[#111] hover:bg-[#161616] p-4 rounded-2xl border border-gray-800 hover:border-[#0BD9E5]/50 transition-all cursor-pointer group">
                              <div className="text-2xl font-black text-gray-700 w-8 text-center group-hover:text-[#0BD9E5] transition-colors">
                                  {item.rank}
                              </div>
                              <div className="w-12 h-12 rounded-full border border-gray-700 overflow-hidden bg-gray-800 shrink-0">
                                  <img src={getAvatarForShadow(getAnonymousName(item.userId))} className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                      <span className="font-bold text-gray-200 text-[15px] truncate">{getAnonymousName(item.userId)}</span>
                                      <div className="flex items-center gap-1.5 text-gray-400">
                                          <Heart size={14} className="group-hover:fill-pink-500 group-hover:text-pink-500 transition-colors" />
                                          <span className="text-sm font-bold">{item.likes || 0}</span>
                                      </div>
                                  </div>
                                  <p className="text-gray-500 text-sm truncate font-medium">{item.content}</p>
                              </div>
                           </div>
                        )
                    })}
                 </div>
             )}
          </div>
        )}
      </div>
    </main>
  );
};
